"use client";

import { useEffect, useMemo } from "react";
import { useUnifiedTradeStore } from "@/store/unifiedTradeStore";
import { useOnchainSwapStore } from "@/store/useOnchainSwapStore";
import {
  ADDRESS_REGEX,
  isSpotMarketCorrect,
  isPerpsMarketCorrect,
  isDexMarketCorrect,
  parseTradePageParams,
  TradePageParams,
} from "@/utils/trading";
import { useSearchParams } from "next/navigation";

// Re-export for backward compatibility
export type { TradePageParams };

/**
 * Hook to parse and apply URL parameters for the Trade Page
 *
 * Supported URL parameters:
 * - type: Trading type (spot | perps | dex), defaults to "spot" for backward compatibility
 * - exchange: Exchange ID (e.g., "kucoin", "hyperliquid")
 * - tokenTo: Base asset symbol (what you're buying)
 * - tokenFrom: Quote asset symbol (what you're selling/paying with), defaults to "USDT"
 * - dexProtocol: DEX protocol (e.g., "aerodrome-cl")
 * - chainId: Chain ID for DEX (e.g., "8453")
 * - tokenOutAddress: Buy token address (tokenOut)
 * - tokenInAddress: Sell token address (tokenIn)
 * - tokenToSymbol / tokenFromSymbol: Optional symbols for DEX display/chart
 *
 * Examples:
 * - /trade?type=spot&exchange=kucoin&tokenTo=KCS&tokenFrom=USDT
 * - /trade?type=perps&exchange=hyperliquid&tokenTo=BTC
 * - /trade?tokenTo=KCS&exchange=kucoin (backward compatible, defaults to spot)
 * - /trade?type=dex&exchange=aerodrome-cl|base&tokenOutAddress=0x...&tokenInAddress=0x...
 */
export function useTradePageParams(): TradePageParams {
  const searchParams = useSearchParams();
  const { setSelectedCexSpotMarket, setSelectedPerpsMarket, setSelectedDexMarket } = useUnifiedTradeStore();
  const { setSelectedDex, setChainId, resetAndSetTokens, setTokenInAddress, setTokenOutAddress } =
    useOnchainSwapStore();

  // Convert searchParams to string for proper dependency tracking
  const searchParamsString = searchParams.toString();

  // Parse URL parameters using the extracted utility function
  const params = useMemo<TradePageParams>(() => parseTradePageParams(searchParams), [searchParamsString]);

  // Check if we have valid URL params that should control market selection
  const hasValidUrlParams = useMemo(() => {
    const { type, exchange, tokenTo, tokenOutAddress, dexProtocol, chainId } = params;
    if (!type) return false;
    if (type === "dex") return !!(tokenOutAddress && dexProtocol && chainId);
    return !!(exchange && tokenTo);
  }, [params]);

  // Apply URL params to store (runs once when params are valid)
  useEffect(() => {
    const { type, exchange, tokenTo, tokenFrom, tokenOutAddress, tokenInAddress, dexProtocol, chainId } = params;

    // Only apply if we have valid params
    if (!hasValidUrlParams) {
      return;
    }

    // Default tokenFrom to USDT if not provided (backward compatibility)
    const quoteAsset = tokenFrom || "USDT";

    // TODO: Validate that the market exists on the exchange before setting it.
    // If the URL specifies a token that doesn't exist on the exchange, we should
    // fall back to default values instead of setting an invalid market.
    // This requires fetching available markets for the exchange and checking if
    // the tokenTo/tokenFrom pair is valid. See PR comment from 0xnikter.
    switch (type) {
      case "spot": {
        if (!exchange || !tokenTo) return;
        const pairSymbol = `${tokenTo}-${quoteAsset}`;
        const currentState = useUnifiedTradeStore.getState().selectedMarket;

        if (!isSpotMarketCorrect(currentState, exchange, tokenTo, quoteAsset)) {
          setSelectedCexSpotMarket(exchange, tokenTo, quoteAsset, pairSymbol);
        }
        break;
      }

      case "perps": {
        if (!exchange || !tokenTo) return;
        const perpSymbol = `${tokenTo}-PERP`;
        const currentState = useUnifiedTradeStore.getState().selectedMarket;

        if (!isPerpsMarketCorrect(currentState, exchange, tokenTo, quoteAsset)) {
          // 5th arg is externalMarketId - use perpSymbol as fallback for URL param navigation
          setSelectedPerpsMarket(perpSymbol, exchange, tokenTo, quoteAsset, perpSymbol);
        }
        break;
      }

      case "dex": {
        if (!tokenOutAddress || !dexProtocol || !chainId) return;

        setSelectedDex(dexProtocol);
        setChainId(chainId);

        if (tokenInAddress && tokenOutAddress) {
          resetAndSetTokens(tokenInAddress, tokenOutAddress);
        } else if (tokenOutAddress) {
          setTokenOutAddress(tokenOutAddress);
        } else if (tokenInAddress) {
          setTokenInAddress(tokenInAddress);
        }

        const baseSymbol = params.tokenToSymbol || (tokenTo && !ADDRESS_REGEX.test(tokenTo) ? tokenTo : "") || "TOKEN";
        const quoteSymbol =
          params.tokenFromSymbol || (tokenFrom && !ADDRESS_REGEX.test(tokenFrom) ? tokenFrom : "") || "TOKEN";
        const pairSymbol = `${baseSymbol}/${quoteSymbol}`;
        const quoteAddress = tokenInAddress || tokenOutAddress;
        const currentState = useUnifiedTradeStore.getState().selectedMarket;

        if (!isDexMarketCorrect(currentState, dexProtocol, chainId, tokenOutAddress, quoteAddress)) {
          setSelectedDexMarket(
            dexProtocol,
            chainId,
            pairSymbol,
            baseSymbol,
            quoteSymbol,
            tokenOutAddress,
            quoteAddress || tokenOutAddress,
          );
        }
        break;
      }
    }
  }, [
    params,
    hasValidUrlParams,
    setSelectedCexSpotMarket,
    setSelectedPerpsMarket,
    setSelectedDexMarket,
    setSelectedDex,
    setChainId,
    resetAndSetTokens,
    setTokenInAddress,
    setTokenOutAddress,
  ]);

  return params;
}
